import { useEffect } from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import {
  CreateExpensableMutationVariables,
  CreateExpensesExpensableRatesInput,
  CreateMileageMutationVariables,
  CreatePerDiemMutationVariables,
  ExpensesEntitiesExpensableResourceTypeEnum,
} from 'generated/resources/graphql'
import { pick } from 'lodash'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { toCents } from 'modules/core/lib/formatCurrencyUtils'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { type ExpenseFormValues } from 'modules/expenses/components/expenses/Detail/_Form/Expense/new/form/form'
import { type MileageFormValues } from 'modules/expenses/components/expenses/Detail/_Form/Mileage/new/form/form'
import { type PerDiemFormValues } from 'modules/expenses/components/expenses/Detail/_Form/PerDiem/new/form/form'
import { getNewFileUploads } from 'modules/expenses/components/expenses/Form/Buttons/helpers'
import { buildErrorMessages } from 'modules/expenses/components/expenses/Form/Buttons/helpers/ActionErrorMessageHandler'
import {
  isExpenseFormValues,
  isMileageFormValues,
  isPerDiemFormValues,
  validatePaymentOption,
} from 'modules/expenses/components/expenses/Form/Buttons/helpers/typechecks'
import {
  normalizeEffectiveOn,
  normalizeTaxes,
  useCreate,
  useCreateMileage,
  useCreatePerDiem,
} from 'modules/expenses/hooks/useExpensable'
import { CustomizationField, Tax } from 'modules/expenses/hooks/useExpensesCustomization'
import {
  SignableOrganizationPerCountry,
  getSignedForOrganization,
} from 'modules/expenses/hooks/useTaxAgencyHomologations'

interface CreateExpenseButtonProps {
  formType: ExpensesEntitiesExpensableResourceTypeEnum
  formValues: ExpenseFormValues | PerDiemFormValues | MileageFormValues
  customizationFields: CustomizationField[]
  onClose: () => void
  buttonType?: 'primary' | 'secondary'
  useDraftLabel?: boolean
  taxAgencyOrganization: SignableOrganizationPerCountry | null
  disabled?: boolean
  onLoadingChange?: (loading: boolean) => void
}

interface Project {
  projectWorker?: { id: string } | null
  subproject?: { id?: string | null } | null
}

const formatProjects = (projects: Project[]) => {
  return projects
    .filter((project) => project.projectWorker?.id)
    .map((project) => ({
      projectWorkerId: project.projectWorker?.id || '0',
      subprojectId: project.subproject?.id,
    }))
}

const CreateFields = [
  'currency',
  'category',
  'subcategory',
  'documentNumber',
  'description',
  'reference',
  'merchantName',
  'merchantTin',
  'paymentMethod',
  'reimbursableCurrency',
  'exchangeRate',
]

const CreatePerDiemFields = [
  'amount',
  'budgetId',
  'category',
  'companyId',
  'currency',
  'description',
  'effectiveOn',
  'employeeId',
  'files',
  'from',
  'groupId',
  'internalReference',
  'projects',
  'rates',
  'resourceType',
  'subcategory',
  'to',
]

const CreateMileageFields = [
  'budgetId',
  'categoryId',
  'companyId',
  'currency',
  'description',
  'effectiveOn',
  'employeeId',
  'files',
  'from',
  'groupId',
  'hasReuseTripOption',
  'internalReference',
  'originalMileageId',
  'projects',
  'rate',
  'subcategory',
  'to',
  'units',
]

// Type guards for Mutation Variables
const isMileageData = (
  data:
    | CreateExpensableMutationVariables
    | CreatePerDiemMutationVariables
    | CreateMileageMutationVariables,
  formType: string
): data is CreateMileageMutationVariables => {
  return formType === 'mileage'
}

const isPerDiemData = (
  data:
    | CreateExpensableMutationVariables
    | CreatePerDiemMutationVariables
    | CreateMileageMutationVariables,
  formType: string
): data is CreatePerDiemMutationVariables => {
  return formType === 'perdiem'
}

const isExpenseData = (
  data:
    | CreateExpensableMutationVariables
    | CreatePerDiemMutationVariables
    | CreateMileageMutationVariables,
  formType: string
): data is CreateExpensableMutationVariables => {
  return formType === 'expense'
}

const CreateExpenseButton = ({
  formType,
  formValues,
  customizationFields,
  onClose,
  buttonType = 'primary',
  useDraftLabel = false,
  taxAgencyOrganization,
  disabled = false,
  onLoadingChange,
}: CreateExpenseButtonProps) => {
  const { company, employee } = useCurrentSession()

  // Strategy pattern for building submit data
  const submitStrategies = {
    expense: (values: ExpenseFormValues): CreateExpensableMutationVariables => {
      if (!('taxes' in values)) {
        throw new Error('Expense strategy requires ExpenseFormValues')
      }

      const formData = pick(values, [...CreateFields])
      const taxes =
        values.taxes?.map((tax: Tax) => ({
          amount: toCents(tax.amount ?? 0),
          base_amount: toCents(tax.base_amount ?? 0),
          percentage: tax.percentage ?? 0,
        })) || []

      const signedForOrganization = getSignedForOrganization({
        filesData: values.files,
        expensable: undefined,
        filesIdToRemove: [],
        taxAgencyOrganization,
      })

      const employeeId = values.employeeId ?? '0'
      return {
        ...formData,
        documentType: values.documentType,
        employeeId: employeeId,
        amount: toCents(values.amount) || null,
        companyId: company.id,
        effectiveOn: normalizeEffectiveOn(values.effectiveOn),
        taxes: normalizeTaxes({
          taxes: taxes,
          vatAmount: taxes[0]?.amount ?? null,
          customizationFields,
        }),
        resourceType: 'Expense',
        projects: formatProjects(values.projects),
        budgetId: values.budgetId,
        reimbursableAmount: values.reimbursableAmount ? toCents(values.reimbursableAmount) : null,
        reimbursableCurrency: values.reimbursableCurrency,
        files: getNewFileUploads(values.files),
        signedForOrganization: signedForOrganization,
        payment: validatePaymentOption(values.payment),
        internalReference: values.internalReference,
        groupId: values.group.id,
        costCenters: values.cost_centers,
      }
    },

    perdiem: (values: PerDiemFormValues): CreatePerDiemMutationVariables => {
      if (!('rates' in values)) {
        throw new Error('PerDiem strategy requires PerDiemFormValues')
      }

      const formatRates = (
        rates: PerDiemFormValues['perDiemRates']
      ): CreateExpensesExpensableRatesInput[] => {
        if (!rates) return []

        // Filter out rates with null or undefined rateConfigurationId
        // This is because the backend expects a rateConfigurationId defined, but we start with an empty rate as a placeholder
        // The user can still submit with the empty rate to save as draft, but we need to filter it out before sending to the backend
        return rates
          .filter(
            (rate): rate is NonNullable<typeof rate> & { rateConfigurationId: string } =>
              rate.rateConfigurationId !== null && rate.rateConfigurationId !== undefined
          )
          .map(
            (rate): CreateExpensesExpensableRatesInput => ({
              rateConfigurationId: rate.rateConfigurationId ?? '0',
              totalAmountCents: rate.totalAmount || 0,
              totalDays: rate.units || 0,
            })
          )
      }

      // Pick only the fields you want to submit
      const formData = pick(values, CreatePerDiemFields)

      const employeeId = values.employeeId ?? '0'
      const response = {
        ...formData,
        category: 'per_diem',
        resourceType: 'PerDiem',
        companyId: company.id,
        employeeId: employeeId,
        currency: values.currency,
        description: values.description,
        effectiveOn: values.start_date,
        endDate: values.end_date,
        files: getNewFileUploads(values.files),
        from: values.from,
        groupId: values.group?.id,
        internalReference: values.internalReference,
        projects: formatProjects(values.projects),
        rates: formatRates(values.perDiemRates),
        startDate: values.start_date,
        subcategory: values.subcategory,
        to: values.to,
        costCenters: values.cost_centers,
      }

      return response
    },

    mileage: (values: MileageFormValues): CreateMileageMutationVariables => {
      // Generate the mutation variables for the mileage form
      const formData = pick(values, CreateMileageFields)

      const originLatitude = values.originLatitude ? parseFloat(values.originLatitude) : null
      const originLongitude = values.originLongitude ? parseFloat(values.originLongitude) : null
      const destinationLatitude = values.destinationLatitude
        ? parseFloat(values.destinationLatitude)
        : null
      const destinationLongitude = values.destinationLongitude
        ? parseFloat(values.destinationLongitude)
        : null
      const roundTrip = values.roundTrip ?? false
      const amount = toCents(values.amount)
      const calculatedMileage = toCents(values.calculatedMileage)
      const mileage = toCents(values.mileage)
      const stops = values.stops?.map(
        (
          stop: { name?: string | null; latitude?: string | null; longitude?: string | null },
          i: number
        ) => ({
          name: stop.name || '',
          latitude: stop.latitude ? parseFloat(stop.latitude) : null,
          longitude: stop.longitude ? parseFloat(stop.longitude) : null,
          position: i,
        })
      )
      const submitData = {
        ...formData,
        companyId: company.id,
        employeeId: values.employeeId ?? '0',
        projects: formatProjects(values.projects),
        files: getNewFileUploads(values.files),
        originLatitude,
        originLongitude,
        destinationLatitude,
        destinationLongitude,
        roundTrip,
        amount,
        calculatedMileage,
        mileage,
        effectiveOn: normalizeEffectiveOn(values.effectiveOn),
        groupId: values.group.id,
        costCenters: values.cost_centers,
        rate: values.rate?.toString(),
        stops,
        originalMileageId: values.originalMileageId ?? undefined,
        hasReuseTripOption: values.hasReuseTripOption ?? false,
      }
      return submitData
    },
  }

  const buildSubmitData = ():
    | CreateExpensableMutationVariables
    | CreatePerDiemMutationVariables
    | CreateMileageMutationVariables => {
    if (formType === 'expense' && isExpenseFormValues(formValues, formType)) {
      return submitStrategies.expense(formValues)
    } else if (formType === 'perdiem' && isPerDiemFormValues(formValues, formType)) {
      return submitStrategies.perdiem(formValues)
    } else if (formType === 'mileage' && isMileageFormValues(formValues, formType)) {
      return submitStrategies.mileage(formValues)
    }

    throw new Error(`No strategy found for form type: ${formType}`)
  }

  const { create, loading } = useCreate()
  const { createPerDiem, loading: loadingPerDiem } = useCreatePerDiem()
  const { createMileage, loading: loadingMileage } = useCreateMileage()

  useEffect(() => {
    onLoadingChange?.(loading || loadingPerDiem || loadingMileage)
  }, [loading, loadingPerDiem, loadingMileage, onLoadingChange])

  const createExpensableExpense = async () => {
    const submitData = buildSubmitData()

    let result
    if (formType === 'perdiem' && isPerDiemData(submitData, formType)) {
      result = await createPerDiem(submitData)
    } else if (formType === 'mileage' && isMileageData(submitData, formType)) {
      result = await createMileage(submitData)
    } else if (formType === 'expense' && isExpenseData(submitData, formType)) {
      result = await create(submitData)
    }

    if (!result) {
      throw new Error(`No result from mutation for form type: ${formType}`)
    }

    const handled = handleResult(result, (errors) => {
      // Need to format the errors so that it can be passed to the setFlash
      const errorMessages = buildErrorMessages(errors)
      app.setFlash({
        type: 'wrong',
        message: errorMessages || i18n.t('errors.generic_error'),
        time: 3000,
      })
    })

    if (!handled.success) return handled

    const expensable = handled.data?.expensable
    if (!expensable) return handled

    app.setFlash({
      type: 'correct',
      message: i18n.t('expenses_v2.modals.new_expense.saved_as_draft_successfully'),
    })

    onClose()
  }

  return (
    <Button
      onClick={createExpensableExpense}
      disabled={loading || disabled}
      loading={loading}
      type={buttonType}
      iconName={loading ? 'upload' : undefined}
    >
      {useDraftLabel
        ? i18n.t('expenses_v2.buttons.save_as_draft')
        : i18n.t('expenses_v2.buttons.save')}
    </Button>
  )
}

export default CreateExpenseButton
